import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PropertyCard from "@/components/site/PropertyCard";
import { LOCALITIES } from "@/data/constants";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X, Home, Store, Factory, Warehouse, MapPin } from "lucide-react";

export default function Properties({ onLoginClick }) {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(sp.entries());
    // translate budget bucket → max/min price
    const b = params.budget;
    delete params.budget;
    if (b === "25") params.max_price = 2500000;
    else if (b === "50") params.max_price = 5000000;
    else if (b === "75") params.max_price = 7500000;
    else if (b === "100") params.max_price = 10000000;
    else if (b === "above") params.min_price = 10000000;
    params.sort = sort;
    api.get("/properties", { params }).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [sp, sort]);

  const setBudget = (v) => {
    const p = new URLSearchParams(sp);
    if (!v || v === "any") p.delete("budget"); else p.set("budget", v);
    p.delete("min_price"); p.delete("max_price");
    setSp(p);
  };

  const update = (k, v) => {
    const p = new URLSearchParams(sp);
    if (!v || v === "any") p.delete(k); else p.set(k, v);
    setSp(p);
  };

  const filterPanel = (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2"><SlidersHorizontal size={18}/> Filters</h3>
      <div>
        <label className="text-xs font-semibold text-slate-600">Purpose</label>
        <Select value={sp.get("purpose") || "any"} onValueChange={(v) => update("purpose", v)}>
          <SelectTrigger data-testid="filter-purpose"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="sale">Buy</SelectItem>
            <SelectItem value="rent">Rent</SelectItem>
            <SelectItem value="lease">Lease</SelectItem>
          </SelectContent>
        </Select>
      </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Category</label>
          <Select value={sp.get("category") || "any"} onValueChange={(v) => update("category", v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="residential">Residential</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
            <SelectItem value="industrial">Industrial</SelectItem>
            <SelectItem value="warehouse">Warehouse</SelectItem>
            <SelectItem value="plot">Plot / Land</SelectItem>
          </SelectContent>
          </Select>
        </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Locality</label>
        <Select value={sp.get("locality") || "any"} onValueChange={(v) => update("locality", v)}>
          <SelectTrigger data-testid="filter-locality"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="any">Any</SelectItem>{LOCALITIES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {sp.get("category") === "residential" && (
        <div>
          <label className="text-xs font-semibold text-slate-600">Bedrooms</label>
          <Select value={sp.get("bedrooms") || "any"} onValueChange={(v) => update("bedrooms", v)}>
            <SelectTrigger data-testid="filter-bedrooms"><SelectValue placeholder="Any"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}+ BHK</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-600">Budget</label>
        <Select value={sp.get("budget") || "any"} onValueChange={(v) => setBudget(v)}>
          <SelectTrigger data-testid="filter-budget"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="25">Up to ₹25 L</SelectItem>
            <SelectItem value="50">Up to ₹50 L</SelectItem>
            <SelectItem value="75">Up to ₹75 L</SelectItem>
            <SelectItem value="100">Up to ₹1 Cr</SelectItem>
            <SelectItem value="above">Above ₹1 Cr</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" className="w-full rounded-full" onClick={() => setSp({})} data-testid="filter-clear">Clear All</Button>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8">
      <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Properties in Surat</h1>
          <p className="text-slate-500 text-sm mt-1">{loading ? "Searching..." : `${items.length} properties found`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="lg:hidden rounded-full" onClick={() => setShowFilter(true)} data-testid="btn-open-filter"><SlidersHorizontal size={14} className="mr-1"/> Filters</Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="price_asc">Price: Low → High</SelectItem>
              <SelectItem value="price_desc">Price: High → Low</SelectItem>
              <SelectItem value="popular">Most Viewed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category chip filters — works across Buy/Rent/Lease */}
      <div className="mb-5 flex flex-wrap gap-2" data-testid="category-chips">
        {[
          { k: "any", label: "All Categories", Icon: SlidersHorizontal },
          { k: "residential", label: "Residential", Icon: Home },
          { k: "commercial", label: "Commercial", Icon: Store },
          { k: "industrial", label: "Industrial", Icon: Factory },
          { k: "warehouse", label: "Warehouse", Icon: Warehouse },
          { k: "plot", label: "Plot / Land", Icon: MapPin },
        ].map(({ k, label, Icon }) => {
          const active = (sp.get("category") || "any") === k;
          return (
            <button
              key={k}
              onClick={() => update("category", k)}
              data-testid={`chip-cat-${k}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${active ? "bg-kp-teal text-white border-kp-teal" : "bg-white text-slate-700 border-slate-200 hover:border-kp-teal hover:text-kp-teal"}`}
            >
              <Icon size={14}/> {label}
            </button>
          );
        })}
        {sp.get("purpose") && (
          <div className="ml-auto self-center text-xs text-slate-500">
            Showing: <span className="font-semibold text-kp-maroon uppercase">{sp.get("purpose") === "sale" ? "Buy" : sp.get("purpose")}</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[300px,1fr] gap-6">
        <aside className="hidden lg:block sticky top-24 self-start">{filterPanel}</aside>
        {showFilter && (
          <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setShowFilter(false)}>
            <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
              <button className="ml-auto block mb-2" onClick={() => setShowFilter(false)}><X/></button>
              {filterPanel}
            </div>
          </div>
        )}
        <div>
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">{Array.from({length:6}).map((_,i) => <div key={i} className="bg-slate-100 rounded-xl h-80 animate-pulse"/>)}</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
              <p className="text-slate-600">No properties match your filters.</p>
              <Button className="mt-4 btn-primary rounded-full" onClick={() => setSp({})}>Clear filters</Button>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((p) => <PropertyCard key={p.id} property={p} onLoginRequired={onLoginClick}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
